import { OutboundCallFeatureFlagId } from '../../../shared/constants/featureFlagIds';
import { spanishTtsVoice } from '../../../shared/constants/sparkConstants/voices';
import { defaultTelephonyDeploymentSettings } from '../../../shared/constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import * as voicifyApi from '../../../shared/api';
import IResult from '../../../shared/models/result/IResult';
import TelephonyDeploymentRequest from '../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import { SPANISH_GRACEFUL_HANGUP_FINAL_MESSAGE, SPANISH_GRACEFUL_HANGUP_WARNING_MESSAGE } from './spanishLineSetup';
import {
    updateApplicationModuleField,
    loadApplicationModuleFields,
    flattenApplicationModuleFields
} from '../../onboarding/api/applicationModuleFields';
import {
    OutboundSchedulePolicyDay,
    OutboundSchedulePolicy,
    defaultOutboundSchedulePolicy,
    generateSchedulePolicyJson,
    parseSchedulePolicyJson
} from '../../../shared/models/outbound/OutboundSchedulePolicy';

export {
    OutboundSchedulePolicyDay,
    OutboundSchedulePolicy,
    defaultOutboundSchedulePolicy,
    generateSchedulePolicyJson,
    parseSchedulePolicyJson
};

export type OutboundProvisioningStep =
    | 'preflight'
    | 'app-settings'
    | 'environment-settings'
    | 'english-outbound-phone'
    | 'spanish-outbound-phone'
    | 'feature-settings'
    | 'schedule-policy'
    | 'module-updates'
    | 'final-activation';

export type OutboundProvisioningStepStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export interface OutboundProvisioningContext {
    applicationId: string;
    environmentId: string;
    organizationName: string;
    locationName: string;
    appName: string;
    pmsName?: string;
    defaultOfficeId?: string;
    timeZone?: string;
    medicalIntegrationConfigId?: string;
    schedulePolicy: OutboundSchedulePolicy;
    hasSpanish: boolean;
    outboundCallingEnabled?: boolean;
    orgId?: string;
    sourceApplicationId?: string;
}

export interface OutboundCloneSource {
    applicationId: string;
    appName: string;
    schedulePolicy: OutboundSchedulePolicy;
}

export interface OutboundProvisioningResult {
    success: boolean;
    errorStep?: OutboundProvisioningStep;
    errorMsg?: string;
}

const SchedulePolicySettingId = 'f3a2b1c0-d4e5-6f7a-8b9c-0d1e2f3a4b5c';
const destinationSpecificSettingIds = new Set([
    'a9c7e3f2-1b74-47cd-a830-942efab7c623', // Medical integration configuration
    'bd2a6d6c-5db2-44f8-91f2-3a22746da78c', // Operatory IDs
    '3167751f-d324-491e-8321-8fbf6911b100', // Procedure type IDs
    '7c8d92e4-5f31-4a2b-9e7c-1d4f6a8b3c5e'  // Provider IDs
]);

const getFeatureFlagId = (flag: any): string | undefined => flag?.id || flag?.featureFlagId;
const getFeatureFlagSettingId = (setting: any): string | undefined => setting?.id || setting?.featureFlagSettingId;
const getOutboundFlag = (value: any) => value?.featureFlags?.find(
    (flag: any) => getFeatureFlagId(flag) === OutboundCallFeatureFlagId
);

const getConfiguredSchedule = (flag: any): OutboundSchedulePolicy | null => {
    const setting = flag?.featureFlagSettings?.find(
        (item: any) => getFeatureFlagSettingId(item) === SchedulePolicySettingId || item?.name === 'Schedule Policy'
    );
    return parseSchedulePolicyJson(setting?.value);
};

const getDefaultScheduleCopy = (): OutboundSchedulePolicy => ({
    dayOfWeekOverrides: defaultOutboundSchedulePolicy.dayOfWeekOverrides.map(day => ({
        ...day,
        timeWindows: [...day.timeWindows],
        retryUnconfirmedTimeWindows: [...day.retryUnconfirmedTimeWindows]
    }))
});

const resolveOutboundConfiguration = async (applicationId: string, seedApplication?: any): Promise<{ outboundFlag: any; schedulePolicy: OutboundSchedulePolicy } | null> => {
    const candidates: any[] = seedApplication ? [seedApplication] : [];
    let fallbackOutboundFlag: any = null;

    try {
        const result = await voicifyApi.findApplication(applicationId);
        if (result.resultType === 'Ok' && result.data) candidates.push(result.data);
    } catch (_) {
        // Continue with expanded application data.
    }

    try {
        const result = await voicifyApi.findApplicationWithFeatures(applicationId);
        if (result.resultType === 'Ok' && result.data) candidates.push(result.data);
    } catch (_) {
        // Continue with environment and inherited-template checks.
    }

    for (const application of candidates) {
        const outboundFlag = getOutboundFlag(application);
        const schedulePolicy = getConfiguredSchedule(outboundFlag);
        if (outboundFlag && schedulePolicy) return { outboundFlag, schedulePolicy };
        if (outboundFlag && !fallbackOutboundFlag) fallbackOutboundFlag = outboundFlag;
    }

    try {
        const environmentsResult = await voicifyApi.getApplicationEnvironments(applicationId);
        if (environmentsResult.resultType === 'Ok' && environmentsResult.data) {
            for (const environment of environmentsResult.data) {
                const outboundFlag = getOutboundFlag(environment);
                const schedulePolicy = getConfiguredSchedule(outboundFlag);
                if (outboundFlag && schedulePolicy) return { outboundFlag, schedulePolicy };
                if (outboundFlag && !fallbackOutboundFlag) fallbackOutboundFlag = outboundFlag;
            }
        }
    } catch (_) {
        // Continue with inherited-template checks.
    }

    try {
        const templatesResult = await voicifyApi.getAppliedApplicationTemplatesForApp(applicationId);
        const templateApplicationIds = templatesResult.resultType === 'Ok'
            ? (templatesResult.data || []).map(item => item?.templateForm?.templateConfiguration?.templateApplicationId).filter(Boolean)
            : [];
        for (const templateApplicationId of templateApplicationIds) {
            const parentResult = await voicifyApi.findApplicationWithFeatures(templateApplicationId);
            if (parentResult.resultType !== 'Ok' || !parentResult.data) continue;
            const outboundFlag = getOutboundFlag(parentResult.data);
            const schedulePolicy = getConfiguredSchedule(outboundFlag);
            if (outboundFlag && schedulePolicy) return { outboundFlag, schedulePolicy };
            if (outboundFlag && !fallbackOutboundFlag) fallbackOutboundFlag = outboundFlag;
        }
    } catch (_) {
        // Continue with the master feature definition.
    }

    try {
        const featureFlagsResult = await voicifyApi.getFeatureFlags();
        if (featureFlagsResult.resultType === 'Ok' && featureFlagsResult.data) {
            const outboundFlag = getOutboundFlag({ featureFlags: featureFlagsResult.data });
            if (outboundFlag) {
                const schedulePolicy = getConfiguredSchedule(outboundFlag);
                if (!schedulePolicy && fallbackOutboundFlag) {
                    return { outboundFlag: fallbackOutboundFlag, schedulePolicy: getDefaultScheduleCopy() };
                }
                return {
                    outboundFlag: fallbackOutboundFlag || outboundFlag,
                    schedulePolicy: schedulePolicy || getDefaultScheduleCopy()
                };
            }
        }
    } catch (_) {
        return null;
    }

    return fallbackOutboundFlag
        ? { outboundFlag: fallbackOutboundFlag, schedulePolicy: getDefaultScheduleCopy() }
        : null;
};

const isCloneableOutboundSetting = (setting: any): boolean => {
    const settingId = getFeatureFlagSettingId(setting);
    if (!setting || settingId === SchedulePolicySettingId || destinationSpecificSettingIds.has(settingId)) return false;
    const name = String(setting.name || '').toLowerCase();
    return !['integration configuration', 'location id', 'caller id', 'callback', 'phone number', 'time zone']
        .some(term => name.includes(term));
};

export const getOutboundCloneSources = async (organizationId: string, currentApplicationId: string): Promise<OutboundCloneSource[]> => {
    if (!organizationId) return [];
    const result = await voicifyApi.getApplicationsWithFeaturesForOrg(organizationId);
    if (result.resultType !== 'Ok' || !result.data) return [];

    const candidates = result.data.filter(application =>
        !!application.id
        && application.id !== currentApplicationId
        && !application.allowsTemplating
        && application.name !== 'Shared Configuration'
        && application.name !== 'Shared Topics'
    );
    const sources = await Promise.all(candidates.map(async application => {
        const deploymentsResult = await voicifyApi.getTelephonyDeployments(application.id).catch(() => null);
        const hasOutboundDeployment = deploymentsResult?.resultType === 'Ok'
            && deploymentsResult.data?.some(deployment => deployment.useForOutboundCalling === true);
        if (!hasOutboundDeployment) return null;

        const configuration = await resolveOutboundConfiguration(application.id, application);
        if (!configuration) return null;
        return {
            applicationId: application.id,
            appName: application.name || 'Unnamed location',
            schedulePolicy: configuration.schedulePolicy
        };
    }));

    return sources
        .filter((source): source is OutboundCloneSource => !!source)
        .sort((left, right) => left.appName.localeCompare(right.appName));
};

export const runOutboundProvisioning = async (
    context: OutboundProvisioningContext,
    onProgress?: (step: OutboundProvisioningStep, status: OutboundProvisioningStepStatus, error?: string) => void
): Promise<OutboundProvisioningResult> => {
    
    const reportProgress = (step: OutboundProvisioningStep, status: OutboundProvisioningStepStatus, error?: string) => {
        if (onProgress) {
            onProgress(step, status, error);
        }
    };

    // Helper to extract error message from IResult
    const getErrorText = (res: IResult<any>, defaultMsg: string): string => {
        if (res.errors && res.errors.length > 0) {
            return res.errors.join(', ');
        }
        return res.resultType || defaultMsg;
    };

    let inboundEnDeployment: any = null;
    let inboundEsDeployment: any = null;
    let existingOutboundEnDeployment: any = null;
    let existingOutboundEsDeployment: any = null;
    let outboundPhoneNumber: string = '';
    let masterOutboundFlag: any = null;
    let sourceOutboundFlag: any = null;
    let sourceModuleFieldLookup: Record<string, any> = {};
    let effectiveSchedulePolicy = context.schedulePolicy;

    let pmsName = context.pmsName || '';
    let defaultOfficeId = context.defaultOfficeId || '';
    let timeZone = context.timeZone || '';
    let medicalIntegrationConfigId = context.medicalIntegrationConfigId || '';

    const mergeLookup = (target: Record<string, any>, fields: any[] = []) => {
        fields.forEach(field => {
            if (!field?.associatedVariable) {
                return;
            }

            target[field.associatedVariable] = {
                ...(target[field.associatedVariable] || {}),
                ...field
            };
        });
    };

    const getAppliedTemplateFieldLookup = async (applicationId: string): Promise<Record<string, any>> => {
        const lookup: Record<string, any> = {};
        const appliedTemplatesResult = await voicifyApi.getAppliedApplicationTemplatesForApp(applicationId);
        if (appliedTemplatesResult.resultType !== 'Ok' || !appliedTemplatesResult.data) {
            return lookup;
        }

        appliedTemplatesResult.data.forEach(appliedTemplate => {
            mergeLookup(lookup, appliedTemplate.templateFormFieldValues || []);
            mergeLookup(lookup, appliedTemplate.dynamicFormFieldValuesCache || []);
        });

        return lookup;
    };

    // STEP 1: Preflight
    try {
        reportProgress('preflight', 'in_progress');

        if (context.sourceApplicationId) {
            const sourceConfiguration = await resolveOutboundConfiguration(context.sourceApplicationId);
            if (!sourceConfiguration) {
                throw new Error('The outbound configuration selected for cloning is no longer available.');
            }
            sourceOutboundFlag = sourceConfiguration.outboundFlag;
            effectiveSchedulePolicy = context.schedulePolicy || sourceConfiguration.schedulePolicy;

            const sourceFieldsResponse = await loadApplicationModuleFields(context.sourceApplicationId, "");
            if (sourceFieldsResponse.result.resultType === 'Ok' && sourceFieldsResponse.parsedData) {
                sourceModuleFieldLookup = flattenApplicationModuleFields(sourceFieldsResponse.parsedData);
            }
        }

        // Fetch application module fields to resolve on-the-fly config values
        let lookup: Record<string, any> = {};
        let sharedLookup: Record<string, any> = {};

        const fieldsResponse = await loadApplicationModuleFields(context.applicationId, "");
        if (fieldsResponse.result.resultType === 'Ok' && fieldsResponse.parsedData) {
            lookup = flattenApplicationModuleFields(fieldsResponse.parsedData);
        }
        mergeLookup(lookup, Object.values(await getAppliedTemplateFieldLookup(context.applicationId)));

        // Robust organization ID fallback lookup
        let orgId = context.orgId;
        if (!orgId) {
            const appResult = await voicifyApi.findApplicationWithFeatures(context.applicationId);
            if (appResult.resultType === 'Ok' && appResult.data) {
                orgId = (appResult.data as any).organizationId;
            }
        }

        if (orgId) {
            const appsResult = await voicifyApi.getApplicationsWithFeaturesForOrg(orgId);
            if (appsResult.resultType === 'Ok' && appsResult.data) {
                const sharedConfigApp = appsResult.data.find(app => app.name === "Shared Configuration") ||
                                       appsResult.data.find(app => app.allowsTemplating) ||
                                       appsResult.data.find(app => app.name === "Shared Topics");
                if (sharedConfigApp && sharedConfigApp.id) {
                    const sharedFieldsResponse = await loadApplicationModuleFields(sharedConfigApp.id, "");
                    if (sharedFieldsResponse.result.resultType === 'Ok' && sharedFieldsResponse.parsedData) {
                        sharedLookup = flattenApplicationModuleFields(sharedFieldsResponse.parsedData);
                    }
                    mergeLookup(sharedLookup, Object.values(await getAppliedTemplateFieldLookup(sharedConfigApp.id)));
                } else {
                    throw new Error("Outbound cannot be configured in Spark due to this org's non-traditional setup. Enable inside app.voicify.");
                }
            }
        }

        const getFieldValueFromObject = (field: any, variableName: string): string => {
            if (!field) return '';
            const values = [field.currentValue, field.value, field.defaultValue];
            for (const val of values) {
                if (val === undefined || val === null) {
                    continue;
                }

                const stringValue = `${val}`.trim();
                if (stringValue !== '' && stringValue !== variableName) {
                    return stringValue;
                }
            }
            return '';
        };

        const getVariableValue = (variableName: string): string => {
            const val = getFieldValueFromObject(lookup[variableName], variableName);
            if (val) {
                return val;
            }
            const sharedVal = getFieldValueFromObject(sharedLookup[variableName], variableName);
            if (sharedVal) {
                return sharedVal;
            }
            return '';
        };

        if (!timeZone) {
            timeZone = getVariableValue('{TIME_ZONE}') || getVariableValue('{Webhook-Time-Zone}') || '';
        }
        if (!defaultOfficeId) {
            defaultOfficeId = getVariableValue('{defaultOfficeId}') || '';
        }
        if (!pmsName) {
            pmsName = getVariableValue('{PMSName}') || '';
        }
        if (!medicalIntegrationConfigId) {
            medicalIntegrationConfigId = getVariableValue('{MedicalIntegrationConfigId}') || '';
        }

        // Validation
        const missingFields: string[] = [];
        if (!timeZone?.trim()) {
            missingFields.push("Time Zone");
        }
        if (!medicalIntegrationConfigId?.trim()) {
            missingFields.push("Configuration ID");
        }
        if (!pmsName?.trim()) {
            missingFields.push("PMS Name");
        }
        if (!defaultOfficeId?.trim()) {
            missingFields.push("Office ID");
        }

        if (missingFields.length > 0) {
            let hasSharedConfigApp = false;
            if (orgId) {
                const appsResult = await voicifyApi.getApplicationsWithFeaturesForOrg(orgId);
                if (appsResult.resultType === 'Ok' && appsResult.data) {
                    hasSharedConfigApp = appsResult.data.some(app => 
                        app.name === "Shared Configuration" || 
                        app.allowsTemplating || 
                        app.name === "Shared Topics"
                    );
                }
            }
            
            if (orgId && !hasSharedConfigApp) {
                throw new Error("Outbound cannot be configured in Spark due to this org's non-traditional setup. Enable inside app.voicify.");
            } else {
                const fieldsText = missingFields.join(", ").replace(/, ([^,]*)$/, " or $1");
                throw new Error(`We were unable to retrieve: ${fieldsText}. Ensure ${missingFields.length === 1 ? 'this value is' : 'these values are'} populated in the location or shared configuration forms and try again.`);
            }
        }

        // Check if callBackNumberOverride module field exists on the location app
        if (!lookup['{callBackNumberOverride}']) {
            console.warn("Unable to update the outbound Call Back Number Override form field. Update manually.");
        }

        // Fetch deployments
        const deploymentsResult = await voicifyApi.getTelephonyDeployments(context.applicationId);
        if (deploymentsResult.resultType !== 'Ok' || !deploymentsResult.data) {
            throw new Error(`Failed to load telephony deployments: ${getErrorText(deploymentsResult, 'Unknown error')}`);
        }

        const list = deploymentsResult.data;
        // Find English and Spanish Inbound
        inboundEnDeployment = list.find(d => d.locale === 'en-US' && !d.useForOutboundCalling);
        inboundEsDeployment = list.find(d => d.locale === 'es-US' && !d.useForOutboundCalling);

        // English and Spanish inbound phone numbers are not strictly required to be configured first.
        // Detect existing Outbound deployments for Idempotency
        existingOutboundEnDeployment = list.find(d => d.locale === 'en-US' && d.useForOutboundCalling);
        existingOutboundEsDeployment = list.find(d => d.locale === 'es-US' && d.useForOutboundCalling);
        outboundPhoneNumber = existingOutboundEnDeployment?.phoneNumber || existingOutboundEsDeployment?.phoneNumber || '';

        // Fetch master feature flags to ensure Outbound flag is present and get its settings schema
        const masterFlagsResult = await voicifyApi.getFeatureFlags();
        if (masterFlagsResult.resultType !== 'Ok' || !masterFlagsResult.data) {
            throw new Error(`Failed to retrieve master feature flags: ${getErrorText(masterFlagsResult, 'Unknown error')}`);
        }
        masterOutboundFlag = masterFlagsResult.data.find(f => getFeatureFlagId(f) === OutboundCallFeatureFlagId);
        if (!masterOutboundFlag) {
            throw new Error("Outbound Calling feature flag schema was not found on the database.");
        }

        reportProgress('preflight', 'completed');
    } catch (e) {
        reportProgress('preflight', 'failed', (e as any).message);
        return { success: false, errorStep: 'preflight', errorMsg: (e as any).message };
    }

    // STEP 2: App Settings
    try {
        reportProgress('app-settings', 'in_progress');

        // Load current application metadata
        const appResponse = await voicifyApi.findApplicationWithFeatures(context.applicationId);
        if (appResponse.resultType !== 'Ok' || !appResponse.data) {
            throw new Error(`Failed to load application metadata: ${getErrorText(appResponse, 'Unknown error')}`);
        }
        const app = appResponse.data;

        // Ensure Outbound flag is present on the application
        const currentFlags = app.featureFlags ?? [];
        const hasOutboundFlagOnApp = currentFlags.some(f => getFeatureFlagId(f) === OutboundCallFeatureFlagId);
        let updatedFlags = [...currentFlags];
        if (!hasOutboundFlagOnApp) {
            updatedFlags.push({
                id: OutboundCallFeatureFlagId,
                name: masterOutboundFlag.name,
                featureFlagSettings: masterOutboundFlag.featureFlagSettings
            });
        }

        const updateRequest = {
            name: app.name,
            shortDescription: app.shortDescription,
            description: app.description,
            keywords: app.keywords,
            invocationPhrase: app.invocationPhrase,
            imageItemId: app.imageItemId,
            timeZone: timeZone,
            featureFlags: updatedFlags,
            OutboundCallIntegrationName: pmsName,
            OutboundCallLocationId: defaultOfficeId,
            pmsName: app.pmsName || pmsName,
            pmsLocationId: app.pmsLocationId || defaultOfficeId,
            manageMenuApplicationId: app.manageMenuApplicationId,
            posName: app.posName,
            posLocationId: app.posLocationId
        };

        const updateResponse = await voicifyApi.updateApplication(context.applicationId, updateRequest);
        if (updateResponse.resultType !== 'Ok') {
            throw new Error(`Failed to update application settings: ${getErrorText(updateResponse, 'Unknown error')}`);
        }

        reportProgress('app-settings', 'completed');
    } catch (e) {
        reportProgress('app-settings', 'failed', (e as any).message);
        return { success: false, errorStep: 'app-settings', errorMsg: (e as any).message };
    }

    // STEP 3: Environment Settings
    try {
        reportProgress('environment-settings', 'in_progress');

        // Load environments
        const envsResponse = await voicifyApi.getApplicationEnvironments(context.applicationId);
        if (envsResponse.resultType !== 'Ok' || !envsResponse.data) {
            throw new Error(`Failed to load environments: ${getErrorText(envsResponse, 'Unknown error')}`);
        }

        const productionEnv = envsResponse.data.find(env => env.name === 'Production');
        if (productionEnv) {
            const currentEnvFlags = productionEnv.featureFlags ?? [];
            const hasOutboundFlagOnEnv = currentEnvFlags.some(f => getFeatureFlagId(f) === OutboundCallFeatureFlagId);
            let updatedEnvFlags = [...currentEnvFlags];
            if (!hasOutboundFlagOnEnv) {
                updatedEnvFlags.push({
                    id: OutboundCallFeatureFlagId,
                    name: masterOutboundFlag.name,
                    featureFlagSettings: masterOutboundFlag.featureFlagSettings
                });
            }

            const updateEnvRequest = {
                name: productionEnv.name,
                applicationName: context.appName,
                featureFlags: updatedEnvFlags
            };

            const updateEnvResponse = await voicifyApi.updateEnvironmentSettings(productionEnv.id, updateEnvRequest);
            if (updateEnvResponse.resultType !== 'Ok') {
                throw new Error(`Failed to update environment settings: ${getErrorText(updateEnvResponse, 'Unknown error')}`);
            }
        }

        reportProgress('environment-settings', 'completed');
    } catch (e) {
        reportProgress('environment-settings', 'failed', (e as any).message);
        return { success: false, errorStep: 'environment-settings', errorMsg: (e as any).message };
    }

    // STEP 4: English Outbound Phone
    try {
        reportProgress('english-outbound-phone', 'in_progress');

        const enOutboundName = `${context.organizationName} - ${context.locationName} - Outbound - EN`;
        const callerId = inboundEnDeployment?.phoneNumber || '';

        const request: TelephonyDeploymentRequest = {
            noTracking: defaultTelephonyDeploymentSettings.noTracking,
            phoneNumberCountryCode: "US",
            phoneNumberStartsWith: "",
            environmentId: context.environmentId,
            ttsOptions: inboundEnDeployment?.ttsOptions?.length > 0 ? inboundEnDeployment.ttsOptions : defaultTelephonyDeploymentSettings.ttsOptions,
            locale: "en-US",
            asrEndpointing: defaultTelephonyDeploymentSettings.asrEndpointing,
            asrProvider: defaultTelephonyDeploymentSettings.asrProvider,
            asrModel: "nova-2",
            asrTier: defaultTelephonyDeploymentSettings.asrTier,
            sonioxContextJson: defaultTelephonyDeploymentSettings.sonioxContextJson,
            performLanguageDetection: false,
            dtmfTurnsToListen: 3,
            ttsPronunciationsMap: defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
            firstMessageDelayMS: defaultTelephonyDeploymentSettings.firstMessageDelayMS,
            mergeTurnsAfterPauses: defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
            mergeTurnTimeoutMS: defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
            disableBargeIn: defaultTelephonyDeploymentSettings.disableBargeIn,
            repromptAttempts: defaultTelephonyDeploymentSettings.repromptAttempts,
            repromptDelaySeconds: defaultTelephonyDeploymentSettings.repromptDelaySeconds,
            name: enOutboundName,
            proactiveResponseDelayMS: defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
            proactiveResponses: defaultTelephonyDeploymentSettings.proactiveResponses,
            useProactiveResponses: false,
            maxProactiveResponseAttempts: defaultTelephonyDeploymentSettings.maxProactiveResponseAttempts,
            followUpProactiveResponses: defaultTelephonyDeploymentSettings.followUpProactiveResponses,
            followUpProactiveResponsesDelayMS: defaultTelephonyDeploymentSettings.followUpProactiveResponsesDelayMS,
            disableWelcomeMessageBargeIn: defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
            disableSpeechStarted: defaultTelephonyDeploymentSettings.disableSpeechStarted,
            speechStartedUnconfirmedBargeInLimit: defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
            speechStartedPauseMS: defaultTelephonyDeploymentSettings.speechStartedPauseMS,
            speechStartedRequiredBufferCount: defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
            callRecordingEnabled: inboundEnDeployment?.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
            speechStartedLevel: defaultTelephonyDeploymentSettings.speechStartedLevel,
            attemptInitialTransfer: defaultTelephonyDeploymentSettings.attemptInitialTransfer,
            initialTransferTimeoutSeconds: defaultTelephonyDeploymentSettings.initialTransferTimeoutSeconds,
            initialTransferTarget: defaultTelephonyDeploymentSettings.initialTransferTarget,
            initialTransferWithCallingNumber: defaultTelephonyDeploymentSettings.initialTransferWithCallingNumber,
            postProactiveResponsePauseDelayMS: defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
            virtualNumber: null,
            useVirtualNumber: false,
            shouldProcessAudio: defaultTelephonyDeploymentSettings.shouldProcessAudio,
            disableSpamDetection: defaultTelephonyDeploymentSettings.disableSpamDetection,
            aiReengageSecondsAfterImmediateTransfer: -1,
            spamDetectionPromptOverride: defaultTelephonyDeploymentSettings.spamDetectionPromptOverride,
            spamDetectionTolerance: defaultTelephonyDeploymentSettings.spamDetectionTolerance,
            spamDetectionTemperature: defaultTelephonyDeploymentSettings.spamDetectionTemperature,
            spamDetectionGenAiProvider: defaultTelephonyDeploymentSettings.spamDetectionGenAiProvider,
            tryWebsocketReconnectionOnWelcome: defaultTelephonyDeploymentSettings.tryWebsocketReconnectionOnWelcome,
            platform: "Telnyx",
            useGenerativeProactiveResponse: defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
            generativeProactiveResponsePrompt: "",
            lastProactiveResponsesToDisallow: defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
            notificationEmails: defaultTelephonyDeploymentSettings.notificationEmails,
            immediateTransferCallerNumbers: defaultTelephonyDeploymentSettings.immediateTransferCallerNumbers,
            outboundCallerIdNumber: callerId,
            outboundCallingEnabled: false, // Leave disabled until the very end
            useForOutboundCalling: true,
            waitForSpeechBeforeWelcome: true,
            disableAssistantSpeechCleaning: defaultTelephonyDeploymentSettings.disableAssistantSpeechCleaning,
            optInText: defaultTelephonyDeploymentSettings.optInText,
            useGracefulRepromptHangup: true,
            gracefulHangupWarningMessage: defaultTelephonyDeploymentSettings.gracefulHangupWarningMessage,
            gracefulHangupFinalMessage: defaultTelephonyDeploymentSettings.gracefulHangupFinalMessage,
            useVoicemailBeepDetection: true,
            enableBackgroundOverlayAudio: defaultTelephonyDeploymentSettings.enableBackgroundOverlayAudio,
            backgroundOverlayAudioUrl: defaultTelephonyDeploymentSettings.backgroundOverlayAudioUrl,
            backgroundOverlayAudioLeadInMS: defaultTelephonyDeploymentSettings.backgroundOverlayAudioLeadInMS
        };

        if (existingOutboundEnDeployment) {
            // Update existing English outbound deployment
            const updateResponse = await voicifyApi.updateTelephonyDeployment(
                context.applicationId,
                existingOutboundEnDeployment.phoneNumber,
                {
                    ...existingOutboundEnDeployment,
                    ...request,
                    outboundCallingEnabled: existingOutboundEnDeployment.outboundCallingEnabled // Keep current enabled state during setup
                }
            );
            if (updateResponse.resultType !== 'Ok') {
                throw new Error(`Failed to update existing English outbound deployment: ${getErrorText(updateResponse, 'Unknown error')}`);
            }
            existingOutboundEnDeployment = updateResponse.data;
            outboundPhoneNumber = existingOutboundEnDeployment?.phoneNumber || outboundPhoneNumber;
        } else {
            // Create new English outbound deployment
            const createResponse = await voicifyApi.addTelephonyDeploymentTelnyx(context.applicationId, request);
            if (createResponse.resultType !== 'Ok' || !createResponse.data) {
                throw new Error(`Failed to create English outbound deployment: ${getErrorText(createResponse, 'Unknown error')}`);
            }
            existingOutboundEnDeployment = createResponse.data;
            outboundPhoneNumber = existingOutboundEnDeployment.phoneNumber;
        }

        reportProgress('english-outbound-phone', 'completed');
    } catch (e) {
        reportProgress('english-outbound-phone', 'failed', (e as any).message);
        return { success: false, errorStep: 'english-outbound-phone', errorMsg: (e as any).message };
    }

    // STEP 5: Spanish Outbound Phone
    try {
        if ((context.hasSpanish ?? !!inboundEsDeployment) && inboundEsDeployment) {
            reportProgress('spanish-outbound-phone', 'in_progress');

            if (!outboundPhoneNumber) {
                throw new Error("English outbound phone number was not created before Spanish outbound setup.");
            }

            const esOutboundName = `${context.organizationName} - ${context.locationName} - Outbound - ES`;
            // Spanish uses the Spanish inbound number as Caller ID
            const callerId = inboundEsDeployment.phoneNumber;

            const request: TelephonyDeploymentRequest = {
                ...existingOutboundEnDeployment,
                noTracking: defaultTelephonyDeploymentSettings.noTracking,
                phoneNumberCountryCode: existingOutboundEnDeployment?.phoneNumberCountryCode || "US",
                phoneNumberStartsWith: outboundPhoneNumber,
                environmentId: context.environmentId,
                ttsOptions: [{ ttsProvider: "ElevenLabs", ttsVoice: spanishTtsVoice }],
                locale: "es-US",
                asrEndpointing: defaultTelephonyDeploymentSettings.asrEndpointing,
                asrProvider: defaultTelephonyDeploymentSettings.asrProvider,
                asrModel: "nova-2",
                asrTier: defaultTelephonyDeploymentSettings.asrTier,
                sonioxContextJson: defaultTelephonyDeploymentSettings.sonioxContextJson,
                performLanguageDetection: false,
                dtmfTurnsToListen: 3,
                ttsPronunciationsMap: defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
                firstMessageDelayMS: defaultTelephonyDeploymentSettings.firstMessageDelayMS,
                mergeTurnsAfterPauses: defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
                mergeTurnTimeoutMS: defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
                disableBargeIn: defaultTelephonyDeploymentSettings.disableBargeIn,
                repromptAttempts: defaultTelephonyDeploymentSettings.repromptAttempts,
                repromptDelaySeconds: defaultTelephonyDeploymentSettings.repromptDelaySeconds,
                name: esOutboundName,
                proactiveResponseDelayMS: defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
                proactiveResponses: "Un momento|Veamos|Solo un momento|Espera un segundo|Espera mientras reviso eso|Dame un momento|Espera mientras miro eso",
                useProactiveResponses: false,
                maxProactiveResponseAttempts: defaultTelephonyDeploymentSettings.maxProactiveResponseAttempts,
                followUpProactiveResponses: "Todavía estoy revisando eso | Sigo investigando esto por ti | Estoy trabajando activamente en esto y te actualizaré pronto | Todavía estoy buscando | Estoy avanzando con esto y te mantendré informado",
                followUpProactiveResponsesDelayMS: defaultTelephonyDeploymentSettings.followUpProactiveResponsesDelayMS,
                disableWelcomeMessageBargeIn: defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
                disableSpeechStarted: defaultTelephonyDeploymentSettings.disableSpeechStarted,
                speechStartedUnconfirmedBargeInLimit: defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
                speechStartedPauseMS: defaultTelephonyDeploymentSettings.speechStartedPauseMS,
                speechStartedRequiredBufferCount: defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
                callRecordingEnabled: inboundEsDeployment.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
                speechStartedLevel: defaultTelephonyDeploymentSettings.speechStartedLevel,
                attemptInitialTransfer: defaultTelephonyDeploymentSettings.attemptInitialTransfer,
                initialTransferTimeoutSeconds: defaultTelephonyDeploymentSettings.initialTransferTimeoutSeconds,
                initialTransferTarget: defaultTelephonyDeploymentSettings.initialTransferTarget,
                initialTransferWithCallingNumber: defaultTelephonyDeploymentSettings.initialTransferWithCallingNumber,
                postProactiveResponsePauseDelayMS: defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
                virtualNumber: null,
                useVirtualNumber: false,
                shouldProcessAudio: defaultTelephonyDeploymentSettings.shouldProcessAudio,
                disableSpamDetection: defaultTelephonyDeploymentSettings.disableSpamDetection,
                aiReengageSecondsAfterImmediateTransfer: -1,
                spamDetectionPromptOverride: defaultTelephonyDeploymentSettings.spamDetectionPromptOverride,
                spamDetectionTolerance: defaultTelephonyDeploymentSettings.spamDetectionTolerance,
                spamDetectionTemperature: defaultTelephonyDeploymentSettings.spamDetectionTemperature,
                spamDetectionGenAiProvider: defaultTelephonyDeploymentSettings.spamDetectionGenAiProvider,
                tryWebsocketReconnectionOnWelcome: defaultTelephonyDeploymentSettings.tryWebsocketReconnectionOnWelcome,
                platform: "Telnyx",
                useGenerativeProactiveResponse: defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
                generativeProactiveResponsePrompt: "",
                lastProactiveResponsesToDisallow: defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
                notificationEmails: defaultTelephonyDeploymentSettings.notificationEmails,
                immediateTransferCallerNumbers: defaultTelephonyDeploymentSettings.immediateTransferCallerNumbers,
                outboundCallerIdNumber: callerId,
                outboundCallingEnabled: false, // Leave disabled until final step
                useForOutboundCalling: true,
                waitForSpeechBeforeWelcome: true,
                disableAssistantSpeechCleaning: defaultTelephonyDeploymentSettings.disableAssistantSpeechCleaning,
                optInText: defaultTelephonyDeploymentSettings.optInText,
                useGracefulRepromptHangup: true,
                gracefulHangupWarningMessage: SPANISH_GRACEFUL_HANGUP_WARNING_MESSAGE,
                gracefulHangupFinalMessage: SPANISH_GRACEFUL_HANGUP_FINAL_MESSAGE,
                useVoicemailBeepDetection: true,
                enableBackgroundOverlayAudio: defaultTelephonyDeploymentSettings.enableBackgroundOverlayAudio,
                backgroundOverlayAudioUrl: defaultTelephonyDeploymentSettings.backgroundOverlayAudioUrl,
                backgroundOverlayAudioLeadInMS: defaultTelephonyDeploymentSettings.backgroundOverlayAudioLeadInMS
            };

            if (existingOutboundEsDeployment) {
                // Update existing Spanish outbound deployment
                const updateResponse = await voicifyApi.updateTelephonyDeployment(
                    context.applicationId,
                    existingOutboundEsDeployment.phoneNumber,
                    {
                        ...existingOutboundEsDeployment,
                        ...request,
                        outboundCallingEnabled: existingOutboundEsDeployment.outboundCallingEnabled
                    }
                );
                if (updateResponse.resultType !== 'Ok') {
                    throw new Error(`Failed to update existing Spanish outbound deployment: ${getErrorText(updateResponse, 'Unknown error')}`);
                }
                existingOutboundEsDeployment = updateResponse.data;
            } else {
                const createResponse = await voicifyApi.addTelephonyDeploymentTelnyx(context.applicationId, request);
                if (createResponse.resultType !== 'Ok' || !createResponse.data) {
                    throw new Error(`Failed to create Spanish outbound deployment: ${getErrorText(createResponse, 'Unknown error')}`);
                }
                existingOutboundEsDeployment = createResponse.data;
            }

            reportProgress('spanish-outbound-phone', 'completed');
        } else {
            reportProgress('spanish-outbound-phone', 'completed');
        }
    } catch (e) {
        reportProgress('spanish-outbound-phone', 'failed', (e as any).message);
        return { success: false, errorStep: 'spanish-outbound-phone', errorMsg: (e as any).message };
    }

    // STEP 6 & 7: Feature Settings & Schedule Policy
    try {
        reportProgress('feature-settings', 'in_progress');
        reportProgress('schedule-policy', 'completed');
        // Get application first to locate current Outbound Call flag settings
        const appResponse = await voicifyApi.findApplicationWithFeatures(context.applicationId);
        if (appResponse.resultType !== 'Ok' || !appResponse.data) {
            throw new Error(`Failed to load app features for settings update: ${getErrorText(appResponse, 'Unknown error')}`);
        }
        const app = appResponse.data;
        const appOutboundFlag = getOutboundFlag(app);

        // Map values onto settings template schema (merged from master Outbound Call schema)
        const masterSettings = masterOutboundFlag.featureFlagSettings || [];
        const masterSettingIds = new Set(masterSettings.map((setting: any) => getFeatureFlagSettingId(setting)));
        const settingsSchema = [
            ...masterSettings,
            ...(sourceOutboundFlag?.featureFlagSettings || []).filter((setting: any) => !masterSettingIds.has(getFeatureFlagSettingId(setting)))
        ];
        const mergedSettings = settingsSchema.map((masterSetting: any) => {
            const settingId = getFeatureFlagSettingId(masterSetting);
            const matchedAppSetting = appOutboundFlag?.featureFlagSettings?.find((setting: any) => getFeatureFlagSettingId(setting) === settingId);
            const matchedSourceSetting = sourceOutboundFlag?.featureFlagSettings?.find((setting: any) => getFeatureFlagSettingId(setting) === settingId);
            const useSourceValue = isCloneableOutboundSetting(matchedSourceSetting) && matchedSourceSetting?.value !== undefined;
            let value = useSourceValue ? matchedSourceSetting.value : (matchedAppSetting?.value || masterSetting.value || '');

            switch (settingId) {
                case 'a9c7e3f2-1b74-47cd-a830-942efab7c623': // Medical Integration Configuration Id
                    value = medicalIntegrationConfigId;
                    break;
                case '689a6a41-e05b-4035-b170-297f453b9a1e': // Leave Voicemail
                    if (!useSourceValue) value = "true";
                    break;
                case '296a74b9-f357-41ed-a933-21b550d89fa6': // Call Customer To Confirm Appointment
                    if (!useSourceValue) value = "true";
                    break;
                case '6557f7ac-2716-4544-8871-9f16790ffce8': // Appointments Scheduled In X Days
                    if (!useSourceValue) value = "2";
                    break;
                case '3430d235-350c-4a25-9ac5-bddd4b9fc5ea': // Call Frequency (in minutes)
                    if (!useSourceValue) value = "30";
                    break;
                case '1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d': // Call Max Attempts
                    if (!useSourceValue) value = "2";
                    break;
                case '8b2f1c5a-9d4e-4a7b-b8c3-2f6a9e1d5b4c': // Do Not Confirm Same Day Appointments
                    if (!useSourceValue) value = "true";
                    break;
                case 'f5e4d3c2-b1a0-9f8e-7d6c-5b4a3928170f': // Call Customer For Re-Activation
                    if (!useSourceValue) value = "true";
                    break;
                case 'a2b3c4d5-e6f7-4839-a0b1-c2d3e4f5a6b7': // Re-Activation - Lookback day
                    if (!useSourceValue) value = "180";
                    break;
                case 'c8fbc6a1-431d-4c42-9c3a-61ca0b1162f7': // Re-Activation - Disable multipatient
                    if (!useSourceValue) value = "true";
                    break;
                case SchedulePolicySettingId: // Schedule Policy
                    value = generateSchedulePolicyJson(effectiveSchedulePolicy);
                    break;
                case 'bd2a6d6c-5db2-44f8-91f2-3a22746da78c': // Operatory Ids
                    // Preserve existing if present, otherwise leave empty/blank
                    value = matchedAppSetting?.value || '';
                    break;
            }

            return {
                ...masterSetting,
                id: masterSetting.id || masterSetting.featureFlagSettingId,
                value
            };
        });

        const requestPayload = {
            featureFlagId: OutboundCallFeatureFlagId,
            featureFlagSettings: mergedSettings
        };

        const response = await voicifyApi.updateApplicationFeatureFlagSettings(context.applicationId, requestPayload);
        if (response.resultType !== 'Ok') {
            throw new Error(`Failed to save outbound feature flag settings: ${getErrorText(response, 'Unknown error')}`);
        }

        reportProgress('feature-settings', 'completed');
    } catch (e) {
        reportProgress('feature-settings', 'failed', (e as any).message);
        return { success: false, errorStep: 'feature-settings', errorMsg: (e as any).message };
    }

    // STEP 8: Module Updates
    try {
        reportProgress('module-updates', 'in_progress');

        // Update callBackNumberOverride module field with English inbound phone number
        const value = inboundEnDeployment?.phoneNumber || '';
        const updateResult = await updateApplicationModuleField(
            context.applicationId,
            '{callBackNumberOverride}',
            value
        );

        if (!updateResult.success) {
            console.warn("Unable to update the outbound Call Back Number Override form field. Update manually.");
        }

        if (context.sourceApplicationId) {
            const reusableScriptVariables = ['{{VoicemailMessage}}', '{ReactivationVoicemail}', '{MissedAppointmentVoicemail}'];
            for (const variable of reusableScriptVariables) {
                const sourceField = sourceModuleFieldLookup[variable];
                const sourceValue = sourceField?.currentValue ?? sourceField?.value ?? sourceField?.defaultValue;
                if (sourceValue && sourceValue !== variable) {
                    const scriptResult = await updateApplicationModuleField(context.applicationId, variable, String(sourceValue));
                    if (!scriptResult.success) {
                        console.warn(`Unable to copy the outbound script for ${variable}. Update manually.`);
                    }
                }
            }
        }

        reportProgress('module-updates', 'completed');
    } catch (e) {
        console.error("Non-fatal module-updates error:", e);
        reportProgress('module-updates', 'completed');
    }

    // STEP 9: Final Activation
    try {
        reportProgress('final-activation', 'in_progress');

        // Cloned configurations begin as drafts; enabling the destination remains explicit.
        const goLive = context.sourceApplicationId ? false : (context.outboundCallingEnabled ?? false);

        // Enable/Disable outbound EN deployment
        if (existingOutboundEnDeployment) {
            const updateEnResponse = await voicifyApi.updateTelephonyDeployment(
                context.applicationId,
                existingOutboundEnDeployment.phoneNumber,
                {
                    ...existingOutboundEnDeployment,
                    outboundCallingEnabled: goLive
                }
            );
            if (updateEnResponse.resultType !== 'Ok') {
                throw new Error(`Failed to activate English outbound deployment: ${getErrorText(updateEnResponse, 'Unknown error')}`);
            }
        }

        // Enable/Disable outbound ES deployment if exists
        if ((context.hasSpanish ?? !!existingOutboundEsDeployment) && existingOutboundEsDeployment) {
            const updateEsResponse = await voicifyApi.updateTelephonyDeployment(
                context.applicationId,
                existingOutboundEsDeployment.phoneNumber,
                {
                    ...existingOutboundEsDeployment,
                    outboundCallingEnabled: goLive
                }
            );
            if (updateEsResponse.resultType !== 'Ok') {
                throw new Error(`Failed to activate Spanish outbound deployment: ${getErrorText(updateEsResponse, 'Unknown error')}`);
            }
        }

        reportProgress('final-activation', 'completed');
    } catch (e) {
        reportProgress('final-activation', 'failed', (e as any).message);
        return { success: false, errorStep: 'final-activation', errorMsg: (e as any).message };
    }

    return { success: true };
};
